<script setup lang="ts">
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import TodoCheckbox from './TodoCheckbox.vue'
import { formatTemporalShort, isOverdue } from '@/todos/dates'
import { useTags } from '@/composables/useTags'
import { isDone } from '@/todos/listView'
import { priorityLevel, priorityMarker } from '@/todos/priority'
import { chipStyle } from '@/todos/tags'
import { countNodes, type TodoNode } from '@/todos/tree'
import { TODO_TREE_CONTEXT } from '@/todos/treeState'

const props = defineProps<{ node: TodoNode; draggable: boolean }>()
const { t } = useI18n()
const context = inject(TODO_TREE_CONTEXT)!
const { colorFor } = useTags()

const todo = computed(() => props.node.todo)
const pending = computed(() => todo.value.uid.startsWith('pending-'))
const done = computed(() => isDone(todo.value))
const overdue = computed(() => isOverdue(todo.value))
const hasChildren = computed(() => props.node.children.length > 0)
const collapsed = computed(() => context.collapsed.value.has(todo.value.uid))
const subtaskProgress = computed(() => {
  const total = countNodes(props.node.children, () => true)
  return total ? `${countNodes(props.node.children, isDone)}/${total}` : ''
})
</script>

<template>
  <div class="todo-row" :class="{ 'is-done': done, 'is-pending': pending }" :data-uid="todo.uid">
    <span v-if="draggable" class="drag-handle" aria-hidden="true">⋮⋮</span>
    <button
      v-if="hasChildren"
      type="button"
      class="collapse"
      :aria-expanded="!collapsed"
      :aria-label="collapsed ? t('row.showSubtasks') : t('row.hideSubtasks')"
      @click="context.toggleCollapsed(todo.uid)"
    >
      {{ collapsed ? '▸' : '▾' }}
    </button>
    <span v-else class="collapse-spacer" />
    <TodoCheckbox
      :status="todo.status"
      :disabled="context.readonly.value || pending"
      @toggle="context.toggleDone(todo.uid)"
    />
    <button type="button" class="body" :disabled="pending" @click="context.open(todo.uid)">
      <span class="summary">
        <span
          v-if="todo.priority"
          class="priority"
          :class="`priority-${priorityLevel(todo.priority)}`"
          >{{ priorityMarker(todo.priority) }}</span
        >
        {{ todo.summary || t('row.untitled') }}
      </span>
      <span class="meta">
        <span v-if="todo.due" class="ui-chip" :class="{ 'ui-chip-danger': overdue }"
          >📅 {{ formatTemporalShort(todo.due) }}</span
        >
        <span v-if="todo.rrule" class="ui-chip">🔁</span>
        <span v-if="todo.alarms.length" class="ui-chip">⏰</span>
        <span v-if="subtaskProgress" class="ui-chip">☑ {{ subtaskProgress }}</span>
        <span v-if="todo.attachments.length" class="ui-chip">📎 {{ todo.attachments.length }}</span>
        <span v-if="todo.comments.length" class="ui-chip">💬 {{ todo.comments.length }}</span>
        <span v-for="assignee in todo.assignees" :key="assignee" class="ui-chip ui-chip-accent">{{
          assignee
        }}</span>
        <span
          v-for="tag in todo.categories"
          :key="tag"
          class="ui-chip tag-link"
          role="button"
          :style="chipStyle(colorFor(tag))"
          :title="t('list.openTag', { tag })"
          :aria-label="t('list.openTag', { tag })"
          @click.stop="context.openTag(tag)"
          ># {{ tag }}</span
        >
        <span v-if="todo.percent_complete && !done" class="ui-chip"
          >{{ todo.percent_complete }}%</span
        >
      </span>
    </button>
    <span v-if="todo.flagged" class="flag" :aria-label="t('row.flagged')">★</span>
    <button
      v-if="!context.readonly.value && !pending"
      type="button"
      class="ui-btn ui-btn-icon add-sub"
      :aria-label="t('row.addSubtask')"
      :title="t('row.addSubtask')"
      @click="context.addSubtask(todo.uid)"
    >
      ＋
    </button>
  </div>
</template>

<style scoped>
.todo-row {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.55rem 0.5rem 0.55rem 0.25rem;
}

.todo-row.is-pending {
  opacity: 0.6;
}

.drag-handle {
  align-self: center;
  color: var(--tg-hint-color);
  cursor: grab;
  touch-action: none;
  user-select: none;
  font-size: 0.8rem;
  letter-spacing: -2px;
}

.collapse,
.collapse-spacer {
  flex: none;
  width: 1.2rem;
  margin-top: 0.1rem;
}

.collapse {
  padding: 0;
  border: none;
  background: none;
  color: var(--tg-hint-color);
  cursor: pointer;
}

.body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.summary {
  overflow-wrap: anywhere;
}

.is-done .summary {
  color: var(--tg-hint-color);
  text-decoration: line-through;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.meta:empty {
  display: none;
}

.tag-link {
  cursor: pointer;
}

.tag-link:hover {
  filter: brightness(0.95);
  text-decoration: underline;
}

.priority {
  margin-right: 0.2rem;
  font-weight: 700;
}

.priority-high {
  color: var(--tg-destructive-text-color);
}

.priority-medium {
  color: #ff9500;
}

.priority-low {
  color: var(--tg-link-color);
}

.flag {
  color: #ff9500;
}

.add-sub {
  color: var(--tg-hint-color);
  font-size: 0.95rem;
}
</style>
